import { unstable_debounce as debouce } from "@mui/utils";

export { debouce };
/**
 * Retorna somente numeros de uma string
 */
export const numbersOnly = (value: string | undefined | null): string => {
  if (typeof value === "string") {
    return value.replace(/[^0-9]/g, "");
  }
  return "";
};

/** DDD + número (10 ou 11 dígitos) — padrão Efi / telefone BR */
export const BRAZIL_PHONE_REGEX = /^[1-9]{2}9?[0-9]{8}$/;

export const BRAZIL_PHONE_INVALID_MSG =
  "Telefone inválido. Informe DDD + número (10 ou 11 dígitos).";

export function isValidBrazilPhone(value: string | undefined | null): boolean {
  const digits = numbersOnly(value);
  if (!digits) return false;
  return BRAZIL_PHONE_REGEX.test(digits);
}

/**
 * Aplicar Mascara CPF
 */
export function cpfApplyMask(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

/**
 * Aplicar Máscara CNPJ
 */
export function cnpjApplyMask(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 14);

  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

/**
 * Aplicar Máscara de CEP
 */
export function cepApplyMask(value: string) {
  const cep = numbersOnly(value);
  return cep.replace(/^(\d{2})(\d{3})(\d{3})$/, "$1.$2-$3");
}

/**
 * Aplicar mascara Telefone
 */
export function phoneApplyMask(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 10) {
    // Telefone fixo: (99) 9999-9999
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }

  // Celular: (99) 99999-9999
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

export function formatCurrency(value: string | number) {
  const toNumber = typeof value === "string" ? Number(value) : value;
  const numberCurrency = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(toNumber);
  return numberCurrency;
}

export function parseBoolean(value: any): boolean {
  if (typeof value === "boolean") {
    // Se já for um booleano, apenas retorne o valor
    return value;
  } else if (typeof value === "number") {
    // Se for um número, considere 1 como true e 0 como false
    return value === 1;
  } else if (typeof value === "string") {
    // Se for uma string, faça a comparação para true ou false
    const lowerCaseValue = value.toLowerCase();
    return lowerCaseValue === "true" || lowerCaseValue === "1";
  } else {
    // Se não for nenhum dos tipos acima, retorne false
    return false;
  }
}

/**
 * Retorna o nome do arquivo de uma URL
 * @param {string | null | undefined} url - URL do arquivo
 * @returns {string} Nome do arquivo
 */
export function getFileNameFromUrl(url: string | null | undefined): string {
  if (!url || url === "" || url === "undefined") return "";
  // Usa o método split para dividir a string com base no "/"
  const parts = url.split("/");
  // Obtém o último elemento do array resultante, que é o nome do arquivo
  const fileName = parts.pop();
  return fileName;
}

/**
 * Converte bytes para Megabytes
 * @param {number} bytes - Tamanho em bytes
 * @returns {string} Tamanho em Megabytes
 */
export function bytesToMB(bytes: number): string {
  // Converte bytes para Megabytes
  const MB = bytes / (1024 * 1024);
  // Formata o resultado para ter duas casas decimais
  return `${MB.toFixed(2)} MB`;
}

/**
 * Validar se o cpf é válido
 */
export function validarCPF(cpf: string): boolean {
  // Remover caracteres não numéricos
  cpf = cpf.replace(/\D/g, "");

  // Verificar se o CPF tem 11 dígitos
  if (cpf.length !== 11) {
    return false;
  }

  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  // Calcular o primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let digitoVerificador1 = resto === 10 || resto === 11 ? 0 : resto;

  // Verificar se o primeiro dígito verificador está correto
  if (digitoVerificador1 !== parseInt(cpf.charAt(9))) {
    return false;
  }

  // Calcular o segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  let digitoVerificador2 = resto === 10 || resto === 11 ? 0 : resto;

  // Verificar se o segundo dígito verificador está correto
  if (digitoVerificador2 !== parseInt(cpf.charAt(10))) {
    return false;
  }

  // CPF válido
  return true;
}

export function calcularPorcentagemDiferenca(
  valueInicial: number,
  valueFinal: number
) {
  const difference = valueFinal - valueInicial;
  const variacaoPositiva = difference > 0; // aumento ou redução
  const percentageChange =
    valueInicial === 0 ? 100 : (difference / valueInicial) * 100;

  const percentageFormated = Math.abs(percentageChange).toFixed(2);
  return {
    percentageFormated: `${percentageFormated}% ${variacaoPositiva ? "↑" : "↓"}`,
    variacaoPositiva,
  };
}

//  Máscara de moeda BRL enquanto digita (mantém somente números e insere vírgula antes dos 2 últimos dígitos)
export function currencyMaskBRL(input: string | number): string {
  const raw = String(input ?? "");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const asNumber = Number(digits);
  const value = asNumber / 100; // 2 casas decimais
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Converte string no formato PT-BR ("1.234,56") para número (1234.56)
export function currencyToNumber(
  value: string | number | null | undefined
): number {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number(value);
  }

  const normalized = numbersOnly(value);

  const n = Number(normalized) / 100;
  return Number.isNaN(n) ? 0 : Number(n);
}

/** Extrai mensagem legível de respostas de erro da API (evita renderizar objeto no React). */
export function resolveApiMessage(
  data: unknown,
  fallback = "Não foi possível concluir a operação."
): string {
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message;
    }
    if (typeof record.error === "string" && record.error.trim()) {
      return record.error;
    }
  }
  return fallback;
}
